import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { SocketRoomsProvider } from './contexts/SocketRoomsContext';
import { useSocketInvalidation } from './hooks/useSocketInvalidation';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import ConstituenciesPage from './pages/ConstituenciesPage';
import CampaignsPage from './pages/CampaignsPage';
import RecordsPage from './pages/RecordsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import CallerLoginPage from './pages/CallerLoginPage';
import CallerDashboard from './pages/CallerDashboard';
import CallCentreManagement from './pages/CallCentreManagement';
import AuditLogPage from './pages/AuditLogPage';
import FiyavalhuPage from './pages/FiyavalhuPage';
import VotingBoxesPage from './pages/VotingBoxesPage';
import PublicTurnoutPage from './pages/PublicTurnoutPage';
import MPRequestsPage from './pages/MPRequestsPage';

function SocketInvalidationHandler() {
  useSocketInvalidation();
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      
      {/* Caller Routes */}
      <Route path="/caller/login" element={<CallerLoginPage />} />
      <Route path="/caller/dashboard" element={<CallerDashboard />} />
      
      {/* Public Routes */}
      <Route path="/turnout" element={<PublicTurnoutPage />} />
      <Route path="/turnout/:campaignId" element={<PublicTurnoutPage />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute permission="dashboard.view">
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/constituencies"
        element={
          <ProtectedRoute permission="constituencies.view">
            <ConstituenciesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute permission="campaigns.view">
            <CampaignsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/records"
        element={
          <ProtectedRoute permission="records.view">
            <RecordsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/call-centre"
        element={
          <ProtectedRoute permission="call_centre.view">
            <CallCentreManagement />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute permission="audit.view">
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/mp-requests"
        element={
          <ProtectedRoute permission="mp_requests.view">
            <MPRequestsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/fiyavalhu"
        element={
          <ProtectedRoute permission="voting.view_boxes">
            <FiyavalhuPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/voting-boxes"
        element={
          <ProtectedRoute permission="voting.view_boxes">
            <VotingBoxesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute permission="users.view">
            <UsersPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute permission="settings.view">
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<LoginPage />} />
    </Routes>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      placeholderData: (previousData) => previousData,
    },
  },
});

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <SocketProvider>
              <SocketRoomsProvider>
                <SocketInvalidationHandler />
                <AppRouter />
              </SocketRoomsProvider>
              <Toaster position="top-right" />
            </SocketProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}

export default App;
